import React, { Fragment, useEffect, useRef, useState } from "react";
import debounce from 'lodash/debounce';
import { Button, FormGroup, Label } from 'reactstrap';
import Select, { components } from "react-select";
import { codeWithColourAutoSuggestion } from "../../../../../services/manage-product/productService";

const AvailableFitTable = (props) => {
    const { setValue, trigger, settingAvailableFitTypes, availableFitRows, setAvailableFitRows } = props;

    // New: duplicate tracking state for fit_type only
    const [duplicateIndices, setDuplicateIndices] = useState([]);

    const [availableInStylesOptions, setAvailableInStylesOptions] = useState([]);
    const [pageAvailableInStyles, setPageAvailableInStyles] = useState(1);
    const [hasMoreAvailableInStyles, setHasMoreAvailableInStyles] = useState(true);
    const [loadingAvailableInStyles, setLoadingAvailableInStyles] = useState(false);
    const [queryAvailableInStyles, setQueryAvailableInStyles] = useState('');


    const fetchAvailableInStylesOptions = async (inputValue, page, currentRowIdx = null) => {
        if (loadingAvailableInStyles || !hasMoreAvailableInStyles) return;
        setLoadingAvailableInStyles(true);
        try {
            // Build exclude_ids from current row's selected fits, excluding the other rows
            const excludeIds = [];
            (availableFitRows || []).forEach((row, i) => {
                if (i !== currentRowIdx) return;
                (row.fits || []).forEach(item => {
                    if (!item) return;
                    const id = typeof item === 'object' ? item.id : item;
                    if (id) excludeIds.push(id);
                });
            });

            let searchData = { q: inputValue, page: page, page_size: 10 };
            if (excludeIds.length) searchData.exclude_ids = Array.from(new Set(excludeIds));

            const response = await codeWithColourAutoSuggestion(searchData);
            const newOptions = response.response;
            setAvailableInStylesOptions(prevOptions => (page === 1 ? newOptions : [...prevOptions, ...newOptions]));
            setHasMoreAvailableInStyles(newOptions.length === 10);
            setPageAvailableInStyles(page + 1);
            setLoadingAvailableInStyles(false);
        } catch (error) {
            setLoadingAvailableInStyles(false);
            console.error('Error fetching availableInStyles options:', error);
        }
    };

    // Debounced fetch
    const debouncedFetchAvailableInStylesOptions = React.useCallback(debounce((inputValue, page, currentRowIdx) => fetchAvailableInStylesOptions(inputValue, page, currentRowIdx), 500), [availableFitRows]);

    // Input change handlers
    const handleInputChangeAvailableInStyles = (inputValue, idx) => {
        setPageAvailableInStyles(1);
        setHasMoreAvailableInStyles(true);
        setQueryAvailableInStyles(inputValue);
        debouncedFetchAvailableInStylesOptions(inputValue, 1, idx);
    };

    // Infinite scroll handlers
    const handleMenuScrollToBottomAvailableInStyles = (idx) => {
        if (hasMoreAvailableInStyles) {
            fetchAvailableInStylesOptions(queryAvailableInStyles, pageAvailableInStyles, idx);
        }
    };

    // Menu open handlers
    const handleMenuOpenAvailableInStyles = (idx) => {
        fetchAvailableInStylesOptions(queryAvailableInStyles, 1, idx);
    };


    const handleAvailableFitRowChange = (idx, field, value) => {
        // If parent fit_type changes, reset dependent 'fits' to avoid stale selections
        if (field === 'fit_type') {
            setAvailableFitRows(prev => prev.map((row, i) => i === idx ? { ...row, fit_type: value, fits: [] } : row));
        } else {
            setAvailableFitRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
        }
    };

    const handleAddAvailableFitRow = () => {
        setAvailableFitRows(prev => ([...prev, { fit_type: '', fits: [] }]));
    };

    const handleRemoveAvailableFitRow = (idx) => {
        setAvailableFitRows(prev => prev.length > 0 ? prev.filter((_, i) => i !== idx) : prev);
    };

    useEffect(() => {
        setValue("available_fits", [...availableFitRows]);
        trigger("available_fits");
    }, [availableFitRows]);

    // New: detect duplicate 'fit_type' values and expose a form-level flag
    useEffect(() => {
        const keyMap = {};
        (availableFitRows || []).forEach((row, idx) => {
            const key = `${row.fit_type || ''}`;
            const isEmpty = !row.fit_type;
            if (isEmpty) return;
            if (!keyMap[key]) keyMap[key] = [];
            keyMap[key].push(idx);
        });
        const duplicates = new Set();
        Object.values(keyMap).forEach(arr => { if (arr.length > 1) arr.forEach(i => duplicates.add(i)); });
        const dupArray = Array.from(duplicates);
        setDuplicateIndices(dupArray);
        const hasDuplicates = dupArray.length > 0;
        if (setValue) setValue("available_fits_has_duplicates", hasDuplicates);
        if (trigger) trigger("available_fits_has_duplicates");
    }, [availableFitRows, setValue, trigger]);

    return (
        <FormGroup>
            <Label className="form-label ai-form-heading">Also Available in Fit (Jumpsuit, Maternity..)</Label>
            <table className="table table-bordered table-sm align-middle">
                <thead>
                    <tr>
                        <th style={{ width: '30%' }}>Type</th>
                        <th style={{ width: '60%' }}>Items</th>
                        <th style={{ width: '10%' }}>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {(availableFitRows || []).map((row, idx) => {
                        const isDuplicate = duplicateIndices.includes(idx);

                        const selectErrorStyles = {
                            control: (base, state) => ({
                                ...base,
                                borderColor: isDuplicate ? '#dc3545' : base.borderColor,
                                boxShadow: isDuplicate ? '0 0 0 1px rgba(220,53,69,0.12)' : base.boxShadow,
                                '&:hover': { borderColor: isDuplicate ? '#dc3545' : base.borderColor },
                            })
                        };

                        return (
                            <tr key={idx}>
                                <td>
                                    <Select
                                        placeholder="Select Fit Type"
                                        options={settingAvailableFitTypes || []}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={settingAvailableFitTypes?.find(opt => opt.id === row.fit_type) || null}
                                        onChange={selected => handleAvailableFitRowChange(idx, 'fit_type', selected ? selected.id : '')}
                                        isClearable={true}
                                        styles={selectErrorStyles}
                                    />
                                </td>
                                <td>
                                    <Select
                                        placeholder="Select Fit Items"
                                        isMulti
                                        options={availableInStylesOptions}
                                        getOptionValue={option => option.id}
                                        getOptionLabel={option => option.name}
                                        value={row.fits}
                                        onChange={selected => handleAvailableFitRowChange(idx, 'fits', selected)}
                                        onInputChange={value => handleInputChangeAvailableInStyles(value, idx)}
                                        onMenuScrollToBottom={() => handleMenuScrollToBottomAvailableInStyles(idx)}
                                        onMenuOpen={() => handleMenuOpenAvailableInStyles(idx)}
                                        isLoading={loadingAvailableInStyles}
                                        isClearable={true}
                                        isDisabled={!row.fit_type} // disable until a fit type is selected
                                    />
                                </td>
                                <td className="text-center">
                                    <Button 
                                        color="danger" 
                                        size="sm" 
                                        onClick={() => handleRemoveAvailableFitRow(idx)} 
                                    ><i className="fa fa-trash" /></Button>
                                    {isDuplicate && <div className="text-danger small mt-1">Duplicate row</div>}
                                </td>
                            </tr>
                        );
                    })}
                 </tbody>
             </table>
             <Button color="success" size="sm" className="mt-2" onClick={handleAddAvailableFitRow}><i className="fa fa-plus" /> Add Row</Button>
         </FormGroup>
     );
 };

 export default AvailableFitTable;
